import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { H1, H4 } from "../../../components/Heading";
import Loading from "../../../components/Loading";
import {
  DataRoomFileOnlineStatus,
  DataRoomFileUploadStatus,
  DealActivityType,
  FileDownloadUrlDocument,
  FileDownloadUrlQuery,
  FileDownloadUrlQueryVariables,
  PermissionsResourceType,
  useDataRoomFileShellQuery,
  useTrackActivityMutation,
} from "../../../graphql/generated";
import useGqlClient from "../../../hooks/useGqlClient";

import { ArrowDownTrayIcon } from "@heroicons/react/20/solid";
import { FileView } from "./FileContent";
import { FileIcon } from "../../../components/FileIcon";
import { NewVersionUploader } from "../../../components/data_room/file_uploaders/NewVersionUploader";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../../../store/dataRoomFile/slice";
import { Button } from "../../../components/tailwind/Button";
import { toasts } from "../../../components/toasts/toasts";
import { StatusDot } from "@/src/components/StatusDot";
import { FilePlus2Icon, HistoryIcon } from "lucide-react";
import { Tabs } from "./Tabs";
import { VersionHistory } from "./VersionHistory";
import { AppState } from "@/src/store";
import { dataRoomSelectors } from "@/src/store/data-room/selector";
import { authSelectors } from "@/src/store/auth/selector";
import { Breadcrumbs } from "@/src/components/data_room/breadcrumbs";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import {
  parseDocumentPageIndex,
  parseDocumentRects,
} from "@/src/utils/documentLocation";
import { BackButton } from "@/src/components/BackButton";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function DataRoomFile() {
  const { id } = useParams<{ id: string }>();
  const client = useGqlClient();
  const dispatch = useDispatch();

  const query = useQuery();

  const [showFileUploader, setShowFileUploader] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const trackActivity = useTrackActivityMutation(client);

  const { dealId } = useParams<{ dealId: string }>();

  const dataRoomFileQuery = useDataRoomFileShellQuery(
    client,
    {
      id,
    },
    {
      enabled: id !== null && id !== "",
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const history = useHistory();

  const deal = useSelector((state: AppState) =>
    authSelectors.deal(state, dealId),
  );

  const dataRoomView = useSelector((state: AppState) =>
    dataRoomSelectors.dataRoomView(state, deal?.dataRoom.id ?? ""),
  );

  useEffect(() => {
    if (!dataRoomFileQuery.data) {
      return;
    }

    if (
      dataRoomFileQuery.data.dataRoomFile.uploadStatus ===
      DataRoomFileUploadStatus.Ready
    ) {
      return;
    }

    const intervalId = setInterval(() => {
      dataRoomFileQuery.refetch();
    }, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    dataRoomFileQuery,
    dataRoomFileQuery.isLoading,
    dataRoomFileQuery.data,
    dataRoomFileQuery.refetch,
  ]);

  useEffect(() => {
    if (!dataRoomFileQuery.data) {
      return;
    }
    trackActivity.mutate({
      type: DealActivityType.FileViewed,
      fileId: id,
      dataRoomId: "",
      folderId: dataRoomFileQuery.data.dataRoomFile.folderId,
    });

    dispatch(
      actions.setSelectedFileVersionId({
        fileId: id,
        versionId: dataRoomFileQuery.data.dataRoomFile.currentLiveVersion.id,
      }),
    );
  }, [id, dataRoomFileQuery.data]);

  if (dataRoomFileQuery.isPending) {
    return <Loading />;
  }

  if (dataRoomFileQuery.error || !dataRoomFileQuery.data) {
    return (
      <div className="w-full">
        <H1>Something went wrong</H1>
      </div>
    );
  }

  const file = dataRoomFileQuery.data.dataRoomFile;

  const rectsOnPage = parseDocumentRects(query.get("rectsOnPage"));
  const pageIndex =
    parseDocumentPageIndex(query.get("pageIndex")) ??
    (rectsOnPage?.length ? 0 : undefined);

  const activeCell = query.get("activeCell") ?? undefined;

  return (
    <div className="w-full relative">
      <div className=" sticky top-0">
        {dataRoomView === "folder" ? (
          <div className="bg-white border-y py-1.5 border-persian-100 z-20 px-2  w-full flex items-center gap-x-2">
            <Breadcrumbs
              folders={file.parentFolders}
              root={`deal/${dealId}/documents`}
              isLoading={dataRoomFileQuery.isLoading}
              p="py-0"
            />
            <p className="text-sm text-gray-400">/</p>
          </div>
        ) : null}
        <div className="bg-white z-20 p-2 w-full">
          <div className="flex items-center justify-between">

            <div className="flex items-center gap-x-2">
              {dataRoomView === "tree" ? (
                <BackButton size="sm" />
              ) : null}
              <FileIcon fileType={file.fileType} size="s" />
              <H4>{file.name}</H4>
              <PermissionCheck input={{ permissionKey: "documents:create" }}>
                <div className="flex items-center gap-x-2">
                  <StatusDot
                    status={
                      file.onlineStatus === DataRoomFileOnlineStatus.Online
                        ? "positive"
                        : "neutral"
                    }
                  />
                  <p className="text-sm text-gray-500">
                    Version {file.currentLiveVersion.versionNumber}
                  </p>
                </div>
              </PermissionCheck>
            </div>
            {!file.deletedAt ? (
              <div className="flex gap-x-2">
                {file.versions.length > 1 ? (
                  <PermissionCheck
                    input={{ permissionKey: "documents:version_history" }}
                  >
                    <Button
                      icon={HistoryIcon}
                      text={
                        showVersionHistory
                          ? "Hide version history"
                          : "Show version history"
                      }
                      variant="neutral"
                      size="s"
                      onClick={() => {
                        setShowVersionHistory(!showVersionHistory);
                      }}
                    />
                  </PermissionCheck>
                ) : null}
                {file.hasDownloadAccess ? (
                  <Button
                    text="Download"
                    variant="neutral"
                    size="s"
                    onClick={() => {
                      client
                        .request<
                          FileDownloadUrlQuery,
                          FileDownloadUrlQueryVariables
                        >(FileDownloadUrlDocument, {
                          id: file.id,
                        })
                        .then((res) => {
                          const downloadUrl = res.fileDownloadUrl.downloadUrl;

                          const a = document.createElement("a");
                          a.href = downloadUrl;
                          a.target = "_blank";

                          document.body.appendChild(a);
                          a.click();

                          document.body.removeChild(a);

                          trackActivity.mutate({
                            type: DealActivityType.FileDownloaded,
                            fileId: id,
                            dataRoomId: "",
                            folderId: file.folderId,
                          });
                        })
                        .catch(() => {
                          toasts.error("Failed to download file");
                        });
                    }}
                    icon={ArrowDownTrayIcon}
                  />
                ) : null}
                <PermissionCheck
                  input={{
                    permissionKey: "documents:edit",
                    resource: {
                      resourceID: file.id,
                      resourceType: PermissionsResourceType.Document,
                    },
                  }}
                >
                  <Button
                    text="New Version"
                    icon={FilePlus2Icon}
                    size="s"
                    variant="positive"
                    onClick={() => {
                      setShowFileUploader(true);
                    }}
                  />
                </PermissionCheck>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="">
        <div className="flex items-center justify-between">


        </div>

        <div className="flex gap-x-4">
          <div className="min-w-0 flex-1">
            <FileView
              file={file}
              pageIndex={pageIndex}
              rectsOnPage={rectsOnPage}
              activeCell={activeCell ? JSON.parse(activeCell) : undefined}
            />
            <div className="mt-4 p-2">
              <Tabs
                fileId={file.id}
                deletedAt={file.deletedAt}
                onlineStatus={file.onlineStatus}
              />
            </div>
          </div>
          {showVersionHistory ? (
            <div className="flex-shrink max-w-[calc(40%)]">
              <VersionHistory fileId={file.id} />
            </div>
          ) : null}
        </div>
      </div>

      <NewVersionUploader
        open={showFileUploader}
        fileName={file.name}
        dataRoomFileId={file.id}
        onClose={() => {
          setShowFileUploader(false);
        }}
      />
    </div>
  );
}
